/**
 * What a `@juisci.com` address is, and what its plus tag means.
 *
 * Isomorphic on purpose. The API grants the trusted-device exemption with this
 * rule (`service/trusted-device.ts`), and the webapp reads the same rule to
 * decide whether to attempt one at all. A second copy in the front-end would be
 * a rule free to drift on the side that grants access.
 */

/** Staff accounts and internal test accounts all live on this domain. */
export const JUISCI_EMAIL_DOMAIN = "juisci.com";

/**
 * The mailbox that a staff address delivers to, or `null` when the address is
 * not one. `ybouhria+test1@juisci.com` gives `ybouhria@juisci.com`, and
 * `ybouhria@juisci.com` gives itself: an untagged address IS its own mailbox.
 *
 * **The domain must be `@juisci.com`.** Elsewhere a `+` is not guaranteed to be
 * a tag. A server that treats it as an ordinary character makes
 * `foo+bar@corp.com` a different mailbox, so a different person.
 *
 * This is why the trusted-device exemption built on it grants nothing new:
 * every address of one mailbox delivers to that mailbox, so whoever proved one
 * of them can already have a code mailed to them for any other. The exemption
 * removes the round trip, not the credential.
 *
 * It answers for the ADDRESS alone. Whether the account behind it may ride a
 * trusted device is a second question, and `service/trusted-device.ts` asks it:
 * an account carrying a role never does.
 */
export function juisciMailboxBase(email: string): string | null {
  const normalized = email.trim().toLowerCase();
  const at = normalized.lastIndexOf("@");
  if (at === -1) return null;

  const local = normalized.slice(0, at);
  if (normalized.slice(at + 1) !== JUISCI_EMAIL_DOMAIN) return null;

  const plus = local.indexOf("+");
  // `+tag@juisci.com` carries a tag and no mailbox in front of it, so there is
  // no base address to bind anything to.
  if (plus === 0) return null;
  if (plus === -1) return normalized;

  return `${local.slice(0, plus)}@${JUISCI_EMAIL_DOMAIN}`;
}
